(ns app.front.view.escrow.account
  (:require [app.domain.fee.escrow-model :as escrow-model]
            [app.domain.file.model :as file-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.section :as section]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.escrow.account-base :as escrow-account-base]
            [app.front.view.escrow.files :as escrow-files]
            [app.front.view.escrow.transactions :as escrow-transactions]
            [app.front.view.fee.events :as fee-events]
            [app.front.view.fee.subs :as fee-subs]
            [app.front.view.payment.checkout :as checkout]
            [app.front.view.workspace.util :as ws-util]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.permission.resolver :as permission-resolver]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [schema.core :as s]))

(def ^:private state-path (interceptors/path ::state))

(rf/reg-sub ::state :-> ::state)

(def tab->view
  {0 :view/escrow-account.outstanding-payments
   1 :view/escrow-account.transactions
   2 :view/escrow-account.attachments})

(rf/reg-event-fx ::change-tab
  (fn [_ [_ tab {{{:keys [account-id]} :path} :parameters
                 :as                          _match}]]
    {:route/push {:route  (get tab->view tab :view/escrow-account.outstanding-payments)
                  :params {:account-id account-id}}}))

(rf/reg-event-db ::set-selected-tab
  [state-path]
  (fn [{state :db} [_ tab]]
    (assoc state :selected-tab tab)))

(rf/reg-sub ::selected-tab
  :<- [::state]
  (fn [state _]
    (or (:selected-tab state)
        0)))

(rf/reg-sub ::workspaces-loading?
  :<- [::state]
  :-> :workspaces-loading?)

(rf/reg-sub ::workspace-ids
  :<- [::state]
  :-> :workspace-ids)

(rf/reg-sub ::workspaces
  :<- [::workspace-ids]
  (fn [workspace-ids]
    @(rf/subscribe [::entities/entities (set workspace-ids)])))

(rf/reg-sub ::workspaces-by-permit-type
  :<- [::workspaces]
  (fn [workspaces]
    (->> workspaces
         (sort-by :workspace/id)
         (group-by :workspace/permit-type))))

(rf/reg-event-fx ::fetch-workspaces
  [state-path]
  (fn [{state :db} [_ account-id]]
    {:comm/command {:command    :fee.escrow/get-workspaces-with-unpaid-bills
                    :method     :get
                    :data       (util/mmap account-id)
                    :on-success [::fetch-workspaces.success]}
     :db           (assoc state :workspaces-loading? true)}))

(rf/reg-event-fx ::fetch-workspaces.success
  [state-path]
  (fn [{state :db} [_ status {:keys [entities]}]]
    (let [workspace-ids (->> entities
                             (keep (fn [[entity-id entity]]
                                     (when (= (:entity/type entity)
                                              :entity.type/workspace)
                                       entity-id))))]
      {:fx [[:dispatch [::entities/merge-entity-db status entities]]]
       :db (-> state
               (assoc :workspaces-loading? false)
               (assoc :workspace-ids workspace-ids))})))

(rf/reg-event-fx ::fetch-transactions
  [state-path]
  (fn [{state :db} [_ account-id]]
    {:comm/command {:command    :fee.escrow/get-all-transactions
                    :method     :get
                    :data       (util/mmap account-id)
                    :on-success [::fetch-transactions.success]}
     :db           (assoc state :transactions-loading? true)}))

(rf/reg-event-fx ::fetch-transactions.success
  [state-path]
  (fn [{state :db} [_ status {:keys [entities]}]]
    {:fx [[:dispatch [::entities/merge-entity-db status entities]]]
     :db (assoc state :transactions-loading? false)}))

(defn- work-type-target-text [tr lang app]
  (let [work-types  (:application/work-type-group-configs app)
        work-target (:application/work-target-group-config app)]
    (if (-> work-types rest seq)
      (tr [:search/multiple-work-types])
      (->> (conj work-types work-target)
           (remove nil?)
           (map #(config-model/localized-name % lang))
           (str/join " / ")))))

(defn- workspaces [tr]
  (if (<sub [::workspaces-loading?])
    [section/section-skeleton]
    (let [workspaces (<sub [::workspaces-by-permit-type])
          lang       (<sub [:language])]
      (for-frag [[permit-type workspaces] workspaces]
        [section/subsection
         {:title (tr [permit-type])}
         [ui-table/modern-responsive-table
          {:items workspaces
           :link  {:description ws-util/workspace-link-description
                   :make-href   ws-util/workspace-or-resume-application-href}
           :tr    tr
           :cols  [[:workspace/address
                    :workspace/address]
                   [:workspace/category
                    #(-> % :workspace/category-group-config (config-model/localized-name lang))]
                   [(case permit-type
                      :permit-type/CR :contractor-registration/trades
                      :business/work-type-target)
                    (fn [workspace]
                      (->> workspace
                           workspace-model/workspace-application
                           (work-type-target-text tr lang)))]
                   [:domain/state
                    #(tr [(-> % workspace-model/workspace-target :domain/state)])]]}]]))))

(defn- account-owner-name-with-municipality [account lang]
  (str (escrow-model/account-owner-name account lang)
       ", "
       (-> account :entity/parent :group/name)))

(rf/reg-event-db ::open-add-funds
  [state-path]
  (fn [state _]
    (assoc state :add-funds-open true)))

(rf/reg-event-db ::close-add-funds
  [state-path]
  (fn [state _]
    (assoc state :add-funds-open false)))

(rf/reg-sub ::add-funds-open?
  :<- [::state]
  :-> :add-funds-open)

(rf/reg-sub ::deposit-amount
  :<- [::state]
  :-> :deposit-amount)

(rf/reg-event-db ::set-deposit-amount
  [state-path]
  (fn [state [_ amount]]
    (assoc state :deposit-amount amount)))

(rf/reg-sub ::deposit-description
  :<- [::state]
  :-> :deposit-description)

(rf/reg-event-db ::set-deposit-description
  [state-path]
  (fn [state [_ description]]
    (assoc state :deposit-description description)))

(rf/reg-event-fx ::render-payment-form
  (fn [{:keys [db]} [_ account-id _ {:keys [form-html transaction-details]}]]
    (if form-html
      {::fee-events/submit-payment-form form-html}
      (merge {:db (-> (assoc db ::checkout/transaction-details transaction-details)
                      (dissoc ::pending-payment))}
             (if (and (ff/enabled? :feature-flag.id/checkout-page-v2)
                      (-> transaction-details :ctx :safe-js? not))
               {:navigate-to (href/href :view/payment-checkout-escrow-v2
                                        {:account-id account-id
                                         :payment-id (-> transaction-details :ctx :payment-id)})}
               {:route/push {:route  :view/payment-checkout-escrow
                             :params {:account-id account-id}}
                :fx         (checkout/payment-service-effects transaction-details)})))))

(rf/reg-event-fx ::initiate-online-payment
  [state-path]
  (fn [{state :db} [_ account-id amount description]]
    {:db           (assoc state ::online-payment-loading? true)
     :comm/command {:command        :payment/get-escrow-deposit-form
                    :data           (p/assoc-if
                                      (util/mmap account-id amount)
                                      :description description)
                    :on-success     [::render-payment-form account-id]
                    :on-bad-request [::fetch-payment-form.error]
                    :on-not-found   [::fetch-payment-form.error]
                    :on-done        [::fetch-payment-form.done]}}))

(rf/reg-event-fx ::fetch-payment-form.error
  [state-path]
  (fn [{state :db} [_ _status body request-id]]
    {:db       (dissoc state ::online-payment-loading?)
     :dispatch [:notifications/add
                :toast
                :error
                :error/error
                (or (:error body) :error-with-req-id/unknown)
                request-id]}))

(rf/reg-event-db ::fetch-payment-form.done
  [state-path]
  (fn [state _]
    (dissoc state ::online-payment-loading?)))

(rf/reg-sub ::account
  (fn [[_ account-id]]
    (rf/subscribe [::entities/entity account-id]))
  :-> identity)

(rf/reg-sub ::has-write-access?
  (fn [[_ account-id]]
    [(rf/subscribe [::account account-id])
     (rf/subscribe [:session/user])])
  (fn [[account user] _]
    (and account
         user
         (permission-resolver/permitted? account user :permission.escrow/admin))))

(rf/reg-sub ::has-files?
  (fn [[_ account-id]]
    (rf/subscribe [::account account-id]))
  (fn [files _]
    (-> files file-model/file-children seq boolean)))

(rf/reg-sub ::show-attachments-tab?
  (fn [[_ account-id]]
    [(rf/subscribe [::has-files? account-id])
     (rf/subscribe [::selected-tab])
     (rf/subscribe [::has-write-access? account-id])])
  (fn [[has-files? selected-tab has-write-access?] _]
    (or has-files? (= 2 selected-tab) has-write-access?)))


(defn add-funds [account tr]
  (let [amount               (<sub [::deposit-amount])
        amount-number        (some-> amount str-util/blank->nil parse-double)
        negative-amount?     (not (pos? amount-number))
        description          (<sub [::deposit-description])
        group                (:entity/parent account)
        currency             (:group/currency group)
        payment-method       (<sub [::fee-subs/online-payment-method (:db/id group)])
        max-amount           (:payment-method/max-amount payment-method)
        max-amount-exceeded? (and amount-number
                                  max-amount
                                  ;: max-amount is stored as cents
                                  (< (/ max-amount 100) amount-number))]
    [ui-add/add-widget {:title   (tr [:escrow.management/add-funds])
                        :buttons [{:text     (tr [:cancel])
                                   :on-click #(>evt [::close-add-funds])}
                                  {:text      (tr [:payment/pay-online])
                                   :disabled? (or (nil? amount-number)
                                                  negative-amount?
                                                  max-amount-exceeded?)
                                   :on-click  #(>evt [::initiate-online-payment
                                                      (:db/id account)
                                                      amount
                                                      description])}]}
     [:div.flex.flex-col.gap-2
      [ui/number-input
       {:id           "deposit-amount"
        :label-text   (tr [:escrow.management/amount])
        :required     true
        :invalid      (or (and amount-number negative-amount?)
                          max-amount-exceeded?)
        :invalid-text (cond
                        max-amount-exceeded? (tr [:fees/online-limit-exceeded]
                                                 [(futil/money-string max-amount currency)])
                        :else (tr [:escrow-account/negative-deposit-amount-help]))
        :unit-label   (-> account :entity/parent :group/currency)
        :on-change    #(rf/dispatch-sync [::set-deposit-amount (futil/tval %)])
        :value        amount}]
      [ui/text-area
       {:id         "deposit-description"
        :label-text (tr [:escrow.management/description])
        :on-change  #(rf/dispatch-sync [::set-deposit-description (futil/tval %)])
        :value      description}]]]))

(defn view [{{{:keys [account-id]}     :path
              {:keys [transaction-id]} :query} :parameters
             :as                               match}]
  (let [lang              (<sub [:language])
        tr                (<sub [:tr])
        account           (<sub [::account account-id])
        currency          (-> account :entity/parent :group/currency)
        account-loading?  (<sub [::escrow-account-base/account-loading?])
        can-deposit?      (and (<sub [::escrow-account-base/deposit-supported?])
                               (contains? (:granted-permissions account)
                                          :permission.escrow/use-for-transactions))
        has-write-access? (<sub [::has-write-access? account-id])
        selected-tab      (<sub [::selected-tab])]
    [:div.cloudpermit-page
     [ui/page-header (util/mmap match)
      [:div.px-8.flex.justify-between
       [:h2 (tr [:escrow/escrow-account])]]]
     (if account-loading?
       [section/section-skeleton]
       [:section.flex.flex-col.w-full
        [:div.mb-6
         [:div.flex.justify-between.items-center
          [:h3.font-bold.mb-3.text-ui-night-sky-100
           (account-owner-name-with-municipality account lang)]
          [:div.font-bold.mb-3.text-ui-night-sky-100
           (str (tr [:escrow-account/id]) ": " (escrow-model/account-number account))]]
         [:div.flex.justify-between
          (tr [:escrow-account/balance])
          [:span.font-bold (futil/money-string-whole-units (:escrow-account/balance account) currency)]]
         (when can-deposit?
           [:<>
            [ui-sep/horizontal]
            (if (<sub [::add-funds-open?])
              [add-funds account tr]
              [ui/button {:id       (str account-id "-add-funds")
                          :kind     :secondary
                          :on-click #(>evt [::open-add-funds])}
               (tr [:escrow.management/add-funds])])])]
        [section/tabbed-section
         {:selected-tab-index selected-tab
          :on-change          #(>evt [::change-tab % match])
          :tabs               (->> [{:label-text (tr [:escrow-account/outstanding-payments])
                                     :content    [workspaces tr]}
                                    {:label-text (tr [:escrow-account/transactions])
                                     :content    [escrow-transactions/transactions
                                                  (util/mmap {:title (tr [:escrow-account/transactions])}
                                                             account-id transaction-id currency tr)]}
                                    (when (<sub [::show-attachments-tab? account-id])
                                      {:label-text (tr [:view/escrow-account.attachments])
                                       :content    [escrow-files/account-files-widget
                                                    (util/mmap tr account-id has-write-access?)]})]
                                   (remove nil?)
                                   (into []))}]])]))

(def outstanding-payments-route
  {:name        :view/escrow-account.outstanding-payments
   :view        view
   :breadcrumb  {:items [:view/escrow-account.outstanding-payments]}
   :parameters  {:path {:account-id s/Int}}
   :controllers [{:parameters {:path [:account-id]}
                  :start      (fn [{{:keys [account-id]} :path}]
                                (>evt [::fetch-workspaces account-id])
                                (>evt [::set-selected-tab 0]))}]})

(def transactions-route
  {:name        :view/escrow-account.transactions
   :view        view
   :breadcrumb  {:items [:view/escrow-account.transactions]}
   :parameters  {:path  {:account-id s/Int}
                 :query {(s/optional-key :transaction-id) s/Int
                         (s/optional-key :notification)   s/Str}}
   :controllers [{:parameters {:path  [:account-id]
                               :query [:tab]}
                  :start      (fn [{{:keys [account-id]} :path}]
                                (>evt [::fetch-transactions account-id])
                                (>evt [::set-selected-tab 1]))}]})

(def attachments-route
  {:name        :view/escrow-account.attachments
   :view        view
   :breadcrumb  {:items [:view/escrow-account.attachments]}
   :controllers [{:start (fn [_]
                           (>evt [::set-selected-tab 2]))}]})
